import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ContactInformation } from '../../model/contact-information';
import { FormControl, Validators } from '@angular/forms';
import { FormUtils } from '../../../shared/utils/form-utils';
import { LocationProvider } from '../../../contacts/patient.model';
import { DEFAULT_ALERT_CONFIG } from '../../../configuration/configuration.defaults';

@Component({
  selector: 'app-contact-dialog',
  templateUrl: './contact-dialog.component.html',
  styleUrls: ['./contact-dialog.component.scss'],
})
export class ContactDialogComponent implements OnInit {
  label!: string;
  name = new FormControl('', [
    Validators.required,
    Validators.pattern(/^[a-zA-Z0-9].*$/),
    Validators.minLength(3),
  ]);
  contactNumber = new FormControl('', [
    Validators.required,
    Validators.pattern(/^\(\d{3}\)\s\d{3}-\d{4}$/),
  ]);

  userId?: string | null;
  readOnlyEdit = false;
  showEditPatientDefaultLocation = false;
  locationEntityId = new FormControl<string>('', [Validators.required]);
  defaultLocationIdControl = new FormControl<string>('', [Validators.required]);

  constructor(
    public dialogRef: MatDialogRef<ContactDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      contact: ContactInformation;
      userId?: string;
      readOnlyEdit?: boolean;
      editPatientTitle?: boolean;
      subjectLabel?: string;
      callbackLocations?: LocationProvider[];
      defaultLocationId?: string;
    },
  ) {
    dialogRef.disableClose = true;
  }

  get editSubjectLabel(): string {
    return this.data.subjectLabel?.trim() || DEFAULT_ALERT_CONFIG.subjectLabel;
  }

  get saveDisabled(): boolean {
    if (this.readOnlyEdit) {
      return true;
    }
    const locationMissing =
      this.showEditPatientDefaultLocation && this.defaultLocationIdControl.invalid;
    return this.contactNumber.invalid || this.name.invalid || locationMissing;
  }

  async ngOnInit() {
    this.readOnlyEdit = !!this.data.readOnlyEdit;
    if (this.data.editPatientTitle) {
      this.contactNumber.setValidators([Validators.pattern(/^\(\d{3}\)\s\d{3}-\d{4}$/)]);
      this.contactNumber.updateValueAndValidity();
    }
    this.showEditPatientDefaultLocation = !!this.data.editPatientTitle;
    this.label = (this.readOnlyEdit || this.data.contact.id) ? 'Edit' : 'New';
    this.name.setValue(this.data.contact.emergencyContactName || this.data.contact.name || null);
    this.contactNumber.setValue(FormUtils.formatPhoneNumber(this.data.contact.contactNumber));
    this.userId = this.data.userId;
    if (this.data.contact.entityId) {
      this.locationEntityId.setValue(this.data.contact.entityId);
    }
    if (this.showEditPatientDefaultLocation) {
      this.defaultLocationIdControl.setValue(this.data.defaultLocationId ?? '');
    }
  }

  getNameErrorMessage() {
    if (this.name.hasError('required')) {
      return 'You must enter a name';
    } else if (this.name.hasError('pattern')) {
      return 'You must enter a valid name';
    } else if (this.name.hasError('minlength')) {
      return 'Name must be at least 3 characters';
    }
    return 'You must enter a valid name';
  }

  public handleKeyUp(event: KeyboardEvent): void {
    if (
      event.key === 'Enter' &&
      ((this.contactNumber.valid && this.data.contact.isPatient && this.name.valid) ||
        (!this.data.contact.isPatient && this.contactNumber.valid))
    ) {
      this.saveDialog();
    } else {
      if (this.contactNumber.value)
        this.contactNumber.setValue(FormUtils.formatPhoneNumber(this.contactNumber.value));
    }
  }

  getContactNumberErrorMessage() {
    if (!this.contactNumber.valid) {
      return 'You must enter a valid phone number';
    }
    return '';
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  saveDialog(): void {
    if (this.readOnlyEdit || this.saveDisabled) {
      return;
    }
    this.dialogRef.close({
      ...this.data.contact,
      entityId: this.locationEntityId.value || this.data.contact.entityId,
      contactNumber: this.contactNumber.value,
      emergencyContactName: this.name.value,
      name: this.name.value,
      fromEpic: false,
      userId: this.userId,
      locationId: this.showEditPatientDefaultLocation
        ? this.defaultLocationIdControl.value || undefined
        : undefined,
    });
  }
}
